import {formatDate, isToday} from 'date-fns';
import {TNotification} from 'entities/notifications';
import {FC, useMemo, useRef} from 'react';
import {
  NotificationItemActions,
  NotificationItemBlock,
  NotificationItemBody,
  NotificationItemDate,
  NotificationItemIcon,
  NotificationItemLink,
  NotificationItemMarkRead,
  NotificationItemText,
  NotificationItemTitle,
} from './styles';
import {
  NotificationCalendarIcon,
  NotificationCO2Icon,
  NotificationHumidityIcon,
  NotificationInfoIcon,
  NotificationInviteIcon,
  NotificationSensorIcon,
  NotificationTemperatureIcon,
} from 'shared/ui/icons';
import {COLORS} from 'shared/consts';
import {NotificationSourceEnum, TNotificationStatus} from 'entities/notifications/model/types';
import {useNavigate} from 'react-router-dom';
import {useHudOverlay} from 'widgets/hud/model/overlayStore';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {$api} from 'shared/api';
import {toast} from 'react-toastify';
import {useApiariesStore} from 'entities/apiaries';
import {useUserStore} from 'entities/user';
import {utcToUserDate} from 'features/calendar/helpers/dates.ts';

type NotificationItemProps = {
  notification: TNotification;
  onItemInView?: (id: string, inView: boolean) => void;
};

export const NotificationItem: FC<NotificationItemProps> = ({notification, onItemInView}) => {
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const markReadRef = useRef<HTMLAnchorElement | null>(null);
  const navigate = useNavigate();
  const close = useHudOverlay((s) => s.close);
  const getApiaries = useApiariesStore((state) => state.getApiaries);
  const userInfo = useUserStore((s) => s.userInfo);

  const date = useMemo(() => {
    const createdAt = new Date(notification.createdAt);

    if (isToday(createdAt)) {
      return formatDate(utcToUserDate(createdAt), 'HH:mm');
    }

    return formatDate(utcToUserDate(createdAt), 'dd.MM.yy, HH:mm');
  }, [notification]);

  const Icon = useMemo(() => {
    switch (notification.source) {
      case NotificationSourceEnum.CALENDAR:
        return NotificationCalendarIcon;
        break;
      case NotificationSourceEnum.HUMIDITY:
        return NotificationHumidityIcon;
        break;
      case NotificationSourceEnum.TEMPERATURE:
        return NotificationTemperatureIcon;
        break;
      case NotificationSourceEnum.CO2:
        return NotificationCO2Icon;
        break;
      case NotificationSourceEnum.SENSOR:
        return NotificationSensorIcon;
        break;
      case NotificationSourceEnum.INVITE:
        return NotificationInviteIcon;
        break;

      default:
        return NotificationInfoIcon;
        break;
    }
  }, [notification]);

  const iconBg = useMemo(() => {
    if (notification.status === TNotificationStatus.READ) return 'transparent';

    switch (notification.source) {
      case NotificationSourceEnum.HUMIDITY:
        return COLORS.ACCENT.CONTAINER_DEFAULT;
        break;
      case NotificationSourceEnum.TEMPERATURE:
        return COLORS.ACCENT.CONTAINER_DEFAULT;
        break;
      case NotificationSourceEnum.CO2:
        return COLORS.ACCENT.CONTAINER_DEFAULT;
        break;

      case NotificationSourceEnum.CALENDAR:
        return COLORS.NEUTRAL.CONTAINER_DEFAULT;
        break;
      case NotificationSourceEnum.SENSOR:
        return COLORS.NEUTRAL.CONTAINER_DEFAULT;
        break;
      case NotificationSourceEnum.INVITE:
        return COLORS.NEUTRAL.CONTAINER_DEFAULT;
        break;

      default:
        return COLORS.ACCENT.CONTAINER_DEFAULT;
        break;
    }
  }, [notification]);

  const bg = useMemo(() => {
    if (notification.status === TNotificationStatus.READ) return 'transparent';

    switch (notification.source) {
      case NotificationSourceEnum.HUMIDITY:
        return COLORS.ACCENT.CONTAINER_MUTED;
        break;
      case NotificationSourceEnum.TEMPERATURE:
        return COLORS.ACCENT.CONTAINER_MUTED;
        break;
      case NotificationSourceEnum.CO2:
        return COLORS.ACCENT.CONTAINER_MUTED;
        break;

      case NotificationSourceEnum.CALENDAR:
        return COLORS.NEUTRAL.CONTAINER_MUTED;
        break;
      case NotificationSourceEnum.SENSOR:
        return COLORS.NEUTRAL.CONTAINER_MUTED;
        break;

      default:
        return COLORS.ACCENT.CONTAINER_MUTED;
        break;
    }
  }, [notification]);

  const onToHiveClick = () => {
    navigate(`/hive/${notification.hive.id}`);
    close();
    onItemInView?.(`${notification.id}`, true);
  };

  const onAcceptInviteClick = async () => {
    const invite = notification.metadata.inviteId;
    if (!invite) return;

    const res = await $api.put(`/invites/${invite}/acceptance`).catch((err) => {
      console.warn(err);
      toast.error('Произошла ошибка, попробуйте позже пожалуйста');
    });

    if (res?.status === 200) {
      onItemInView?.(`${notification.id}`, true);
      toast.success('Вы успешно добавлены на пасеку');
      if (userInfo) {
        await getApiaries(userInfo?.id);
      }
    }
  };

  const ActionLinks = useMemo(() => {
    if (notification.source === NotificationSourceEnum.CALENDAR) {
      return () => (
        <NotificationItemLink>
          <span>К событию</span>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.5577 11.4693L10.0577 5.99982L8.99996 7.06343L13.9652 12.0011L8.99996 16.9387L10.0577 18.0023L15.5577 12.5329C15.6992 12.3921 15.7788 12.2007 15.7788 12.0011C15.7788 11.8014 15.6992 11.61 15.5577 11.4693Z"
              fill="#FF4F12"
            />
          </svg>
        </NotificationItemLink>
      );
    }

    if (notification.source === NotificationSourceEnum.INVITE) {
      if (notification.status === TNotificationStatus.READ) return () => null;
      return () => (
        <>
          <Button label="Принять" onClick={onAcceptInviteClick} />
          <Button
            label="Отклонить"
            variant="secondary"
            colorScheme="neutral"
            onClick={() => onItemInView?.(`${notification.id}`, true)}
          />
        </>
      );
    }

    if (notification.hive?.id) {
      return () => (
        <NotificationItemLink onClick={onToHiveClick}>
          <span>К улью</span>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.5577 11.4693L10.0577 5.99982L8.99996 7.06343L13.9652 12.0011L8.99996 16.9387L10.0577 18.0023L15.5577 12.5329C15.6992 12.3921 15.7788 12.2007 15.7788 12.0011C15.7788 11.8014 15.6992 11.61 15.5577 11.4693Z"
              fill="#FF4F12"
            />
          </svg>
        </NotificationItemLink>
      );
    }

    return () => null;
  }, [notification]);

  return (
    <NotificationItemBlock ref={notificationRef}>
      <NotificationItemIcon $bg={iconBg}>
        <Icon />
      </NotificationItemIcon>
      <NotificationItemBody $bg={bg}>
        <NotificationItemTitle>{notification.title}</NotificationItemTitle>
        <NotificationItemText>{notification.text}</NotificationItemText>
        <NotificationItemActions>
          <ActionLinks />
          {onItemInView && notification.status === TNotificationStatus.NEW && (
            <NotificationItemMarkRead
              ref={markReadRef}
              onClick={() => onItemInView(`${notification.id}`, true)}
            >
              <span>Отметить прочитанным</span>
            </NotificationItemMarkRead>
          )}
        </NotificationItemActions>
        <NotificationItemDate>{date}</NotificationItemDate>
      </NotificationItemBody>
    </NotificationItemBlock>
  );
};
